import { FormEvent, useEffect, useMemo, useState } from "react";

type Guess = { word: string; rank: number; closeness: number; hint?: boolean; unlisted?: boolean };
type GuessResponse = (Guess & { status: "accepted"; lemma: string }) | {
  status: "suggestion" | "invalid";
  word: string;
  suggestions: string[];
  message: string;
};
type Puzzle = { id: number; date: string; gameNumber: number; totalWords: number; round: number; totalRounds: number; variant: number };
type Score = { name: string; guesses: number; seconds: number; playedAt: string };
type View = "game" | "stats" | "leaderboard" | "help" | "admin";
type AdminQuestion = {
  id: string;
  source: "built-in" | "semantic" | "curated" | "custom";
  date?: string;
  answer: string;
  closest: string[];
  acceptedWords: number;
  pos?: string;
  frequency?: number;
  originalAnswer?: string;
  words?: [string, number][];
  overridden?: boolean;
  semanticOverride?: boolean;
  curatedOverride?: boolean;
  category?: string;
};
type DictionaryWord = { word: string; lemma: string; pos: string; frequency: number; semantic: boolean; canBeAnswer: boolean };
type DictionaryStats = { dictionaryEntries: number; acceptedForms: number; lemmas: number; semanticLemmas: number; answerPool: number };

const api = async <T,>(url: string, init?: RequestInit): Promise<T> => {
  const res = await fetch(url, { ...init, headers: { "Content-Type": "application/json", ...(init?.headers || {}) } });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "هەڵەیەک ڕوویدا");
  return data;
};

const kuNum = (n: number) => new Intl.NumberFormat("ckb").format(n);
const posLabel = (pos?: string) => ({ noun: "ناو", verb: "کردار", adj: "هاوەڵناو", adv: "هاوەڵکار", propn: "ناوی تایبەت", pron: "جێناو" }[pos || ""] || "وشە");
const storageKey = (date: string, round: number, variant = 0) => `wata:${date}:${round}:${variant}`;
const variantKey = (date: string, round: number) => `wata:variant:${date}:${round}`;
const progressKey = (date: string) => `wata:round:${date}`;
const attemptsKey = (date: string) => `wata:attempts:${date}`;
const startedKey = (date: string) => `wata:started:${date}`;
const getClientId = () => {
  const saved = localStorage.getItem("wata:clientId");
  if (saved) return saved;
  const created = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  localStorage.setItem("wata:clientId", created);
  return created;
};

function Icon({ name }: { name: string }) {
  const icons: Record<string, string> = { stats: "◔", trophy: "♕", help: "؟", sun: "☼", hint: "✦", refresh: "↻", edit: "✎", share: "↗", flame: "♨", close: "×" };
  return <span aria-hidden="true">{icons[name]}</span>;
}

export default function App() {
  const [view, setView] = useState<View>(() => window.location.pathname.replace(/\/+$/, "") === "/admin" ? "admin" : "game");
  const [puzzle, setPuzzle] = useState<Puzzle | null>(null);
  const [guesses, setGuesses] = useState<Guess[]>([]);
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [spelling, setSpelling] = useState<{ word: string; suggestions: string[]; message: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [startedAt, setStartedAt] = useState(Date.now());
  const [clientId] = useState(getClientId);
  const [leaderboard, setLeaderboard] = useState<Score[]>([]);
  const [toast, setToast] = useState("");
  const [dark, setDark] = useState(() => localStorage.getItem("wata:theme") === "dark");
  const solved = guesses.some(g => g.rank === 1);
  const best = useMemo(() => guesses.length ? Math.min(...guesses.map(g => g.rank)) : 99999, [guesses]);

  useEffect(() => { document.documentElement.dataset.theme = dark ? "dark" : "light"; localStorage.setItem("wata:theme", dark ? "dark" : "light"); }, [dark]);
  useEffect(() => {
    let active = true;
    const initialize = async () => {
      try {
        const first = await api<Puzzle>(`/api/puzzle?clientId=${encodeURIComponent(clientId)}&round=0`);
        const savedRound = Math.max(0, Math.min(first.totalRounds - 1, Number(localStorage.getItem(progressKey(first.date))) || 0));
        const savedVariant = Math.max(0, Number(localStorage.getItem(variantKey(first.date, savedRound))) || 0);
        const current = savedRound === 0 && savedVariant === 0
          ? first
          : await api<Puzzle>(`/api/puzzle?clientId=${encodeURIComponent(clientId)}&round=${savedRound}&variant=${savedVariant}`);
        if (!active) return;
        setPuzzle(current);
        const saved = localStorage.getItem(storageKey(current.date, current.round, current.variant));
        setGuesses(saved ? JSON.parse(saved) : []);
        const dailyStart = Number(localStorage.getItem(startedKey(current.date))) || Date.now();
        localStorage.setItem(startedKey(current.date), String(dailyStart));
        setStartedAt(dailyStart);
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : "هەڵە");
      }
    };
    initialize();
    return () => { active = false; };
  }, [clientId]);
  useEffect(() => { if (puzzle) localStorage.setItem(storageKey(puzzle.date, puzzle.round, puzzle.variant), JSON.stringify(guesses)); }, [guesses, puzzle]);
  useEffect(() => { if (view === "leaderboard") api<Score[]>("/api/leaderboard").then(setLeaderboard).catch(() => {}); }, [view]);

  const scoreWord = async (word: string) => {
    setError(""); setSpelling(null);
    if (!word || solved) return;
    if (guesses.some(g => g.word === word)) return setError("ئەم وشەیەت پێشتر تاقی کردووەتەوە");
    setLoading(true);
    try {
      const result = await api<GuessResponse>("/api/guess", { method: "POST", body: JSON.stringify({ word, clientId, round: puzzle?.round || 0, variant: puzzle?.variant || 0 }) });
      if (result.status !== "accepted") {
        setSpelling({ word: result.word, suggestions: result.suggestions, message: result.message });
        return;
      }
      const g: Guess = result;
      const next = [...guesses, g].sort((a, b) => a.rank - b.rank); setGuesses(next); setValue("");
      if (g.rank === 1 && puzzle) {
        const seconds = Math.round((Date.now() - startedAt) / 1000);
        const totalAttempts = (Number(localStorage.getItem(attemptsKey(puzzle.date))) || 0) + next.length;
        localStorage.setItem(attemptsKey(puzzle.date), String(totalAttempts));
        if (puzzle.round === puzzle.totalRounds - 1) {
          await api("/api/result", { method: "POST", body: JSON.stringify({ guesses: totalAttempts, seconds, clientId, round: puzzle.round }) });
          updateStats(totalAttempts);
        }
      }
    } catch (e) { setError(e instanceof Error ? e.message : "هەڵە"); } finally { setLoading(false); }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    await scoreWord(value.trim());
  };

  const getHint = async () => {
    if (solved) return; setLoading(true); setError("");
    try {
      const g = await api<Guess>("/api/hint", { method: "POST", body: JSON.stringify({ bestRank: best, used: guesses.map(x => x.word), clientId, round: puzzle?.round || 0, variant: puzzle?.variant || 0 }) });
      setGuesses([...guesses.filter(x => x.word !== g.word), { ...g, hint: true }].sort((a, b) => a.rank - b.rank));
    } catch (e) { setError(e instanceof Error ? e.message : "هەڵە"); } finally { setLoading(false); }
  };

  const nextQuestion = async () => {
    if (!puzzle || puzzle.round >= puzzle.totalRounds - 1 || loading) return;
    const nextRound = puzzle.round + 1;
    setLoading(true); setError("");
    try {
      const nextVariant = Math.max(0, Number(localStorage.getItem(variantKey(puzzle.date, nextRound))) || 0);
      const next = await api<Puzzle>(`/api/puzzle?clientId=${encodeURIComponent(clientId)}&round=${nextRound}&variant=${nextVariant}`);
      localStorage.setItem(progressKey(next.date), String(nextRound));
      setPuzzle(next);
      const saved = localStorage.getItem(storageKey(next.date, next.round, next.variant));
      setGuesses(saved ? JSON.parse(saved) : []);
      setValue("");
      setSpelling(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e instanceof Error ? e.message : "هەڵە");
    } finally {
      setLoading(false);
    }
  };

  const refreshQuestion = async () => {
    if (!puzzle || solved || loading) return;
    const nextVariant = puzzle.variant + 1;
    setLoading(true); setError(""); setSpelling(null);
    try {
      const next = await api<Puzzle>(`/api/puzzle?clientId=${encodeURIComponent(clientId)}&round=${puzzle.round}&variant=${nextVariant}`);
      localStorage.setItem(variantKey(next.date, next.round), String(next.variant));
      setPuzzle(next);
      const saved = localStorage.getItem(storageKey(next.date, next.round, next.variant));
      setGuesses(saved ? JSON.parse(saved) : []);
      setValue("");
      setToast("پرسیارەکە گۆڕدرا");
      setTimeout(() => setToast(""), 1800);
    } catch (e) {
      setError(e instanceof Error ? e.message : "هەڵە");
    } finally {
      setLoading(false);
    }
  };

  const updateStats = (count: number) => {
    const old = JSON.parse(localStorage.getItem("wata:stats") || '{"played":0,"wins":0,"streak":0,"best":0}');
    const last = localStorage.getItem("wata:lastWin");
    const yesterday = new Date(Date.now() - 864e5).toISOString().slice(0, 10);
    const streak = last === yesterday ? old.streak + 1 : last === puzzle?.date ? old.streak : 1;
    localStorage.setItem("wata:stats", JSON.stringify({ played: old.played + 1, wins: old.wins + 1, streak, best: old.best ? Math.min(old.best, count) : count }));
    if (puzzle) localStorage.setItem("wata:lastWin", puzzle.date);
  };

  const share = async () => {
    const bars = guesses.slice(0, 8).map(g => g.rank === 1 ? "🟩" : g.rank <= 20 ? "🟢" : g.rank <= 100 ? "🟡" : "⬜").join("");
    const totalAttempts = puzzle ? Number(localStorage.getItem(attemptsKey(puzzle.date))) || guesses.length : guesses.length;
    const text = `واتا #${puzzle?.gameNumber} — ${kuNum(puzzle?.totalRounds || 10)} پرسیار تەواو\n${kuNum(totalAttempts)} هەوڵ\n${bars}\nواتا؛ مانا ڕێگات پیشان دەدات.`;
    try { if (navigator.share) await navigator.share({ text }); else await navigator.clipboard.writeText(text); setToast("ئەنجامەکە کۆپی کرا"); setTimeout(() => setToast(""), 2200); } catch { /* cancelled */ }
  };

  const closeAdmin = () => {
    window.history.replaceState({}, "", "/");
    setView("game");
  };

  return <div className="app-shell">
    <header className="topbar">
      <button className="brand" onClick={() => setView("game")} aria-label="پەڕەی سەرەکی"><span className="brand-mark">و</span><span><strong>واتا</strong><small>مانا ڕێگات پیشان دەدات</small></span></button>
      <nav aria-label="بەشەکان">
        <button onClick={() => setView("stats")} aria-label="ئامار"><Icon name="stats" /></button>
        <button onClick={() => setView("leaderboard")} aria-label="پلەبەندی"><Icon name="trophy" /></button>
        <button onClick={() => setView("help")} aria-label="ڕێنمایی"><Icon name="help" /></button>
        <button onClick={() => setDark(!dark)} aria-label="گۆڕینی ڕووکار"><Icon name="sun" /></button>
      </nav>
    </header>

    <main>
      {view === "game" && <section className="game-view">
        <div className="eyebrow"><span>یاریی تێکەڵی ئەمڕۆ</span><b>#{kuNum(puzzle?.gameNumber || 0)}</b></div>
        <div className="round-progress" aria-label="پێشکەوتنی پرسیارەکان">
          <strong>پرسیاری {kuNum((puzzle?.round || 0) + 1)} لە {kuNum(puzzle?.totalRounds || 10)}</strong>
          <div><i style={{ width: `${(((puzzle?.round || 0) + (solved ? 1 : 0)) / (puzzle?.totalRounds || 10)) * 100}%` }} /></div>
        </div>
        <h1>وشە نهێنییەکە بدۆزەوە</h1>
        <p className="lede">هەر وشەیەکی کوردی بنووسە؛ ژمارەی بچووکتر واتە لە ماناکە نزیکتری.</p>
        <div className="game-card">
          {!solved ? <form onSubmit={submit} className="guess-form">
            <label htmlFor="guess">هەوڵی تۆ</label>
            <div><input id="guess" value={value} onChange={e => { setValue(e.target.value); setSpelling(null); }} placeholder="وشەیەک بنووسە..." autoComplete="off" autoFocus /><button disabled={loading || !value.trim()}>تاقی بکەرەوە <span>←</span></button></div>
            {error && <p className="error" role="alert">{error}</p>}
            {spelling && <div className={`spelling-card ${spelling.suggestions.length ? "has-suggestions" : "invalid-word"}`} role="status">
              <strong>{spelling.message}</strong>
              {spelling.suggestions.length > 0 && <div>{spelling.suggestions.map(suggestion => <button type="button" key={suggestion} onClick={() => scoreWord(suggestion)} disabled={loading}>بەڵێ، «{suggestion}»</button>)}</div>}
              <button type="button" className="dismiss-spelling" onClick={() => { setSpelling(null); setValue(""); }}>وشەیەکی تر دەنووسم</button>
            </div>}
          </form> : <div className="win-panel"><span className="spark">✦</span><h2>پیرۆزە! دۆزیتەوە</h2><p>بە {kuNum(guesses.length)} هەوڵ گەیشتیتە وشە نهێنییەکە.</p>{puzzle && puzzle.round < puzzle.totalRounds - 1
            ? <button onClick={nextQuestion} disabled={loading}>پرسیاری دواتر <span>←</span></button>
            : <><strong className="daily-complete">هەموو پرسیارەکانی ئەمڕۆت تەواو کرد</strong><button onClick={share}><Icon name="share" /> ئەنجامەکە هاوبەش بکە</button></>}</div>}
          <div className="toolbar"><div><strong>{kuNum(guesses.length)}</strong><span>هەوڵ</span></div><div><strong>{best === 99999 ? "—" : kuNum(best)}</strong><span>باشترین پلە</span></div><div className="toolbar-actions"><button onClick={refreshQuestion} disabled={loading || solved} title="ئەم پرسیارە بگۆڕە"><Icon name="refresh" /> گۆڕینی پرسیار</button><button onClick={getHint} disabled={loading || solved}><Icon name="hint" /> ئاماژە</button></div></div>
        </div>
        <div className="history-head"><h2>مێژووی هەوڵەکان</h2><span>{guesses.length ? "باشترینەکان لە سەرەوەن" : "هێشتا هیچ هەوڵێک نییە"}</span></div>
        <div className="guess-list" aria-live="polite">
          {guesses.map((g, i) => <div className={`guess-row ${g.rank === 1 ? "exact" : g.rank <= 20 ? "hot" : g.rank <= 100 ? "warm" : "cool"}`} key={g.word} style={{ animationDelay: `${i * 30}ms` }}>
            <span className="rank">{kuNum(g.rank)}</span><strong>{g.word} {g.hint && <small title="ئاماژە">✦</small>}</strong><div className="meter"><i style={{ width: `${Math.max(4, g.closeness * 100)}%` }} /></div><span className="proximity">٪{kuNum(Math.round(g.closeness * 100))}</span>
          </div>)}
        </div>
      </section>}
      {view === "stats" && <Stats onClose={() => setView("game")} />}
      {view === "leaderboard" && <Panel title="پلەبەندی ئەمڕۆ" onClose={() => setView("game")}><div className="leaderboard">{leaderboard.map((s, i) => <div key={i}><b>{kuNum(i + 1)}</b><span>{s.name || "یاریکەرێکی نەناسراو"}</span><strong>{kuNum(s.guesses)} هەوڵ</strong><small>{kuNum(s.seconds)} چرکە</small></div>)}{!leaderboard.length && <p className="empty">هێشتا هیچ ئەنجامێک تۆمار نەکراوە.</p>}</div></Panel>}
      {view === "help" && <Panel title="چۆن یاری بکەم؟" onClose={() => setView("game")}><div className="help"><p>هەر ڕۆژ ١٠ وشەی نهێنیی ئاسان و باوی سۆرانیی عێراقیت بۆ هەڵدەبژێردرێت؛ ئامانج دۆزینەوەیانە بە پەیوەندیی مانا، نەک پیتەکان.</p><ol><li><b>وشەیەک بنووسە.</b> هەر وشەیەکی کوردیی ناسراو قبوڵ دەکرێت.</li><li><b>پلەکە بخوێنەوە.</b> پلەی ١ واتە وەڵامی ڕاست؛ ژمارەی بچووکتر نزیکترە.</li><li><b>لە پەیوەندییەکان بیر بکەرەوە.</b> جۆر، شوێن و بەکارهێنانی شتەکان یارمەتیت دەدەن.</li><li><b>گیرایت؟</b> ئاماژە بەکاربهێنە یان بە «گۆڕینی پرسیار» پرسیارێکی پەسەندکراوی تر وەربگرە.</li></ol><p className="note">هەوڵەکان سنووریان نییە و پێشکەوتنی ١٠ پرسیارەکەت لەم ئامێرەدا پاشەکەوت دەبێت.</p></div></Panel>}
      {view === "admin" && <Admin onClose={closeAdmin} />}
    </main>
    {toast && <div className="toast">{toast}</div>}
    <footer><span>واتا — یارییەکی سەربەخۆی کوردی</span><span>هەر ڕۆژ ١٠ پرسیاری تێکەڵ</span></footer>
  </div>;
}

function Panel({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return <section className="panel"><div className="panel-title"><div><span>واتا</span><h1>{title}</h1></div><button onClick={onClose} aria-label="داخستن"><Icon name="close" /></button></div>{children}</section>;
}

function Stats({ onClose }: { onClose: () => void }) {
  const s = JSON.parse(localStorage.getItem("wata:stats") || '{"played":0,"wins":0,"streak":0,"best":0}');
  return <Panel title="ئامارەکانی من" onClose={onClose}><div className="stat-grid"><div><b>{kuNum(s.played)}</b><span>یاری</span></div><div><b>٪{kuNum(s.played ? Math.round(s.wins / s.played * 100) : 0)}</b><span>بردنەوە</span></div><div><b>{kuNum(s.streak)}</b><span>زنجیرە <Icon name="flame" /></span></div><div><b>{s.best ? kuNum(s.best) : "—"}</b><span>کەمترین هەوڵ</span></div></div><p className="note">ئەم ئامارانە لەسەر ئەم ئامێرە پاشەکەوت دەبن.</p></Panel>;
}

function Admin({ onClose }: { onClose: () => void }) {
  const [logged, setLogged] = useState(false);
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [questions, setQuestions] = useState<AdminQuestion[]>([]);
  const [questionSearch, setQuestionSearch] = useState("");
  const [questionTotal, setQuestionTotal] = useState(0);
  const [questionPage, setQuestionPage] = useState(0);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [dictionaryWords, setDictionaryWords] = useState<DictionaryWord[]>([]);
  const [dictionarySearch, setDictionarySearch] = useState("");
  const [dictionaryTotal, setDictionaryTotal] = useState(0);
  const [dictionaryPage, setDictionaryPage] = useState(0);
  const [dictionaryStats, setDictionaryStats] = useState<DictionaryStats | null>(null);
  const [loadingDictionary, setLoadingDictionary] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<AdminQuestion | null>(null);
  const [editForm, setEditForm] = useState({ target: "", words: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [replacingQuestion, setReplacingQuestion] = useState<string | null>(null);
  const [updateFile, setUpdateFile] = useState<File | null>(null);
  const [updating, setUpdating] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), target: "", words: "" });
  const pageSize = 50;
  useEffect(() => { api<{ authenticated: boolean }>("/api/admin/session").then(x => setLogged(x.authenticated)).catch(() => {}); }, []);
  useEffect(() => {
    if (!logged) return;
    let active = true;
    setLoadingQuestions(true);
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams({ q: questionSearch.trim(), offset: String(questionPage * pageSize), limit: String(pageSize) });
      api<{ total: number; builtIn: AdminQuestion[]; custom: AdminQuestion[]; dictionary: DictionaryStats }>(`/api/admin/questions?${params}`)
        .then(result => { if (active) { setQuestions([...result.custom, ...result.builtIn]); setQuestionTotal(result.total); setDictionaryStats(result.dictionary); } })
        .catch(e => { if (active) setMessage(e instanceof Error ? e.message : "هەڵەیەک لە هێنانی پرسیارەکان ڕوویدا"); })
        .finally(() => { if (active) setLoadingQuestions(false); });
    }, 250);
    return () => { active = false; window.clearTimeout(timer); };
  }, [logged, questionSearch, questionPage]);
  useEffect(() => {
    if (!logged) return;
    let active = true;
    setLoadingDictionary(true);
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams({ q: dictionarySearch.trim(), offset: String(dictionaryPage * pageSize), limit: String(pageSize) });
      api<{ total: number; items: DictionaryWord[]; stats: DictionaryStats }>(`/api/admin/dictionary?${params}`)
        .then(result => { if (active) { setDictionaryWords(result.items); setDictionaryTotal(result.total); setDictionaryStats(result.stats); } })
        .catch(e => { if (active) setMessage(e instanceof Error ? e.message : "هەڵەیەک لە هێنانی فەرهەنگەکە ڕوویدا"); })
        .finally(() => { if (active) setLoadingDictionary(false); });
    }, 250);
    return () => { active = false; window.clearTimeout(timer); };
  }, [logged, dictionarySearch, dictionaryPage]);
  const login = async (e: FormEvent) => { e.preventDefault(); try { await api("/api/admin/login", { method: "POST", body: JSON.stringify({ password }) }); setLogged(true); setMessage(""); } catch (e) { setMessage(e instanceof Error ? e.message : "هەڵە"); } };
  const save = async (e: FormEvent) => { e.preventDefault(); try {
    await api("/api/admin/puzzles", { method: "POST", body: JSON.stringify(form) });
    const result = await api<{ total: number; builtIn: AdminQuestion[]; custom: AdminQuestion[] }>("/api/admin/questions?offset=0&limit=50");
    setQuestions([...result.custom, ...result.builtIn]);
    setQuestionTotal(result.total);
    setQuestionPage(0);
    setMessage("پرسیارەکە بە سەرکەوتوویی پاشەکەوت کرا.");
  } catch (e) { setMessage(e instanceof Error ? e.message : "هەڵە"); } };

  const beginEdit = (question: AdminQuestion) => {
    const words = question.words?.length
      ? question.words.map(([word, score]) => `${word}:${score}`).join("\n")
      : question.closest.map((word, index) => `${word}:${Math.max(0.5, 0.95 - index * 0.08).toFixed(2)}`).join("\n");
    setEditingQuestion(question);
    setEditForm({ target: question.answer, words });
    setMessage("");
  };

  const saveEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingQuestion || savingEdit) return;
    setSavingEdit(true); setMessage("");
    try {
      const result = await api<{ target: string; words: [string, number][]; closest: string[] }>(`/api/admin/questions/${encodeURIComponent(editingQuestion.id)}`, {
        method: "PUT",
        body: JSON.stringify({ target: editForm.target, words: editForm.words, originalAnswer: editingQuestion.originalAnswer || editingQuestion.answer }),
      });
      setQuestions(current => current.map(question => question.id === editingQuestion.id ? {
        ...question,
        answer: result.target,
        closest: result.closest,
        words: result.words,
        overridden: question.source !== "custom" || question.overridden,
      } : question));
      setEditingQuestion(null);
      setMessage("دەستکاریی پرسیارەکە پاشەکەوت کرا.");
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "دەستکاریی پرسیارەکە سەرکەوتوو نەبوو");
    } finally {
      setSavingEdit(false);
    }
  };

  const replaceQuestion = async (question: AdminQuestion) => {
    if (replacingQuestion) return;
    setReplacingQuestion(question.id); setMessage("");
    try {
      const result = await api<{ target: string; words: [string, number][]; closest: string[]; semanticOverride: boolean; curatedOverride: boolean }>(`/api/admin/questions/${encodeURIComponent(question.id)}/replace`, {
        method: "POST",
        body: JSON.stringify({ answer: question.answer, originalAnswer: question.originalAnswer || question.answer }),
      });
      setQuestions(current => current.map(item => item.id === question.id ? {
        ...item,
        answer: result.target,
        closest: result.closest,
        words: result.words,
        overridden: true,
        semanticOverride: result.semanticOverride,
        curatedOverride: result.curatedOverride,
      } : item));
      if (editingQuestion?.id === question.id) setEditingQuestion(null);
      setMessage("پرسیارەکە بە پرسیارێکی ئاسان‌تر گۆڕدرا.");
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "گۆڕینی پرسیارەکە سەرکەوتوو نەبوو");
    } finally {
      setReplacingQuestion(null);
    }
  };

  const logout = async () => {
    try { await api("/api/admin/logout", { method: "POST", body: "{}" }); } catch { /* the local session is cleared below */ }
    setLogged(false); setPassword(""); setQuestions([]); setEditingQuestion(null); setMessage("");
  };

  const installUpdate = async (e: FormEvent) => {
    e.preventDefault();
    if (!updateFile || updating) return;
    if (!updateFile.name.toLowerCase().endsWith(".zip")) {
      setMessage("تکایە تەنها فایلی ZIP ـی فەرمی واتا هەڵبژێرە.");
      return;
    }
    if (!window.confirm("دڵنیایت لە دامەزراندنی ئەم نوێکردنەوەیە؟ پێش جێگیرکردن backup دروست دەکرێت.")) return;

    setUpdating(true);
    setMessage("فایلەکە بار دەکرێت و پشکنین دەکرێت…");
    try {
      const response = await fetch("/api/admin/update", {
        method: "POST",
        headers: { "Content-Type": "application/zip" },
        body: updateFile,
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "نوێکردنەوە سەرکەوتوو نەبوو");
      setMessage("نوێکردنەوە جێگیر کرا. واتا خۆکارانە هەڵدەستێتەوە؛ تکایە چەند چرکەیەک چاوەڕێ بکە.");
      setTimeout(() => window.location.reload(), 12000);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "نوێکردنەوە سەرکەوتوو نەبوو");
      setUpdating(false);
    }
  };

  return <Panel title="بەڕێوەبردنی واتا" onClose={onClose}>
    {!logged ? <form className="admin-form" onSubmit={login}>
      <p>بۆ بەڕێوەبردنی واتا، وشەی نهێنیی بەڕێوەبەر بنووسە.</p>
      <label>وشەی نهێنی<input type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" /></label>
      <button>چوونەژوورەوە</button>
      {message && <p className="error">{message}</p>}
    </form> : <div className="admin-sections">
      <section className="admin-session-bar"><span>بە شێوەی بەڕێوەبەر چوویتە ژوورەوە</span><button type="button" onClick={logout}>چوونەدەرەوە</button></section>
      <form className="admin-form" onSubmit={save}>
        <div className="admin-heading"><span>١</span><div><h2>زیادکردنی پرسیار</h2><p>وشەی نهێنی و وشە نزیکەکانی بەروارێکی دیاریکراو دابنێ.</p></div></div>
        <label>بەرواری یاری<input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></label>
        <label>وشەی نهێنی<input value={form.target} onChange={e => setForm({ ...form, target: e.target.value })} placeholder="نموونە: باران" /></label>
        <label>وشە نزیکەکان و خاڵی مانایی<textarea rows={10} value={form.words} onChange={e => setForm({ ...form, words: e.target.value })} placeholder={"هەور:0.95\nئاو:0.82\nزستان:0.70"} /></label>
        <small>لە هەر دێڕێکدا «وشە:خاڵ» بنووسە؛ خاڵ لە ٠ تا ١.</small>
        <button>پاشەکەوتکردنی پرسیار</button>
      </form>

      <section className="admin-form question-bank">
        <div className="admin-heading"><span>٢</span><div><h2>هەموو پرسیار و وەڵامەکان</h2><p>لێرە هەموو وشە نهێنییەکان، وەڵامی ڕاست و نزیکترین وشەکانیان دەبینیت.</p></div></div>
        <div className="question-bank-toolbar">
          <input value={questionSearch} onChange={e => { setQuestionSearch(e.target.value); setQuestionPage(0); }} placeholder="گەڕان بە وەڵامی ڕاست…" aria-label="گەڕان لە پرسیارەکان" />
          <b>{loadingQuestions ? "…" : `${kuNum(questions.length)} لە ${kuNum(questionTotal)}`}</b>
        </div>
        <div className="question-list">
          {questions.map((question, index) => <article className="question-row" key={question.id}>
            <div className="question-number">{kuNum(questionPage * pageSize + index + 1)}</div>
            <div className="question-content">
              <div><span className={`question-source ${question.source}`}>{question.source === "custom" ? "زیادکراوی ئەدمین" : question.overridden ? "دەستکاریکراو" : question.source === "curated" ? "پەسەندکراوی سۆرانی" : question.source === "semantic" ? "خۆکاری مانایی" : "بنەڕەتی"}</span>{question.date && <small>{String(question.date).slice(0, 10)}</small>}{question.category && <small>{question.category}</small>}{question.pos && <small>{posLabel(question.pos)}</small>}<span className="question-actions"><button type="button" className="edit-question-button" onClick={() => beginEdit(question)}><Icon name="edit" /> دەستکاری</button><button type="button" className="replace-question-button" onClick={() => replaceQuestion(question)} disabled={Boolean(replacingQuestion)}><Icon name="refresh" /> {replacingQuestion === question.id ? "دەگۆڕدرێت…" : "گۆڕین"}</button></span></div>
              <strong>وەڵامی ڕاست: <mark>{question.answer}</mark></strong>
              <p>نزیکترین وشەکان: {question.closest.join("، ") || "—"}</p>
              {editingQuestion?.id === question.id && <form className="question-edit-form" onSubmit={saveEdit}>
                <label>وەڵامی ڕاست<input value={editForm.target} onChange={e => setEditForm({ ...editForm, target: e.target.value })} /></label>
                <label>وشە نزیکەکان و خاڵ<textarea rows={7} value={editForm.words} onChange={e => setEditForm({ ...editForm, words: e.target.value })} /></label>
                <small>لانیکەم ٣ وشەی کوردی؛ نموونە: «هەور:0.95»</small>
                <div><button disabled={savingEdit}>{savingEdit ? "پاشەکەوت دەکرێت…" : "پاشەکەوتکردن"}</button><button type="button" className="cancel-edit" onClick={() => setEditingQuestion(null)}>پاشگەزبوونەوە</button></div>
              </form>}
            </div>
          </article>)}
          {!loadingQuestions && !questions.length && <p className="empty question-empty">هیچ پرسیارێک نەدۆزرایەوە.</p>}
        </div>
        <div className="admin-pagination">
          <button type="button" onClick={() => setQuestionPage(page => Math.max(0, page - 1))} disabled={loadingQuestions || questionPage === 0}>پەڕەی پێشوو</button>
          <span>پەڕەی {kuNum(questionPage + 1)} لە {kuNum(Math.max(1, Math.ceil(questionTotal / pageSize)))}</span>
          <button type="button" onClick={() => setQuestionPage(page => page + 1)} disabled={loadingQuestions || (questionPage + 1) * pageSize >= questionTotal}>پەڕەی دواتر</button>
        </div>
      </section>

      <section className="admin-form dictionary-bank">
        <div className="admin-heading"><span>٣</span><div><h2>فەرهەنگی کوردی</h2><p>فەرهەنگە گەورەکە تەنها بۆ ناسینەوەی هەوڵ و چاککردنەوەی ڕێنووس بەکاردێت؛ وەڵامەکان لە بانکی پەسەندکراون.</p></div></div>
        {dictionaryStats && <div className="dictionary-summary">
          <div><b>{kuNum(dictionaryStats.acceptedForms)}</b><span>شێوەی وشەی ناسراو</span></div>
          <div><b>{kuNum(dictionaryStats.lemmas)}</b><span>وشەی بنەڕەتی</span></div>
          <div><b>{kuNum(dictionaryStats.semanticLemmas)}</b><span>وشەی مانایی</span></div>
          <div><b>{kuNum(dictionaryStats.answerPool)}</b><span>پرسیاری پەسەندکراو</span></div>
        </div>}
        <div className="question-bank-toolbar">
          <input value={dictionarySearch} onChange={e => { setDictionarySearch(e.target.value); setDictionaryPage(0); }} placeholder="گەڕان لە فەرهەنگ…" aria-label="گەڕان لە فەرهەنگی کوردی" />
          <b>{loadingDictionary ? "…" : `${kuNum(dictionaryWords.length)} لە ${kuNum(dictionaryTotal)}`}</b>
        </div>
        <div className="dictionary-list">
          {dictionaryWords.map((item, index) => <article className="dictionary-row" key={`${item.word}-${index}`}>
            <strong>{item.word}</strong>
            <span>{item.lemma === item.word ? "هەمان بنەڕەت" : `بنەڕەت: ${item.lemma}`}</span>
            <small>{posLabel(item.pos)}</small>
            <div>{item.semantic && <i>مانایی</i>}{item.canBeAnswer && <i className="answer-ready">گونجاوی پرسیار</i>}</div>
          </article>)}
          {!loadingDictionary && !dictionaryWords.length && <p className="empty question-empty">ئەم وشەیە لە فەرهەنگدا نەدۆزرایەوە.</p>}
        </div>
        <div className="admin-pagination">
          <button type="button" onClick={() => setDictionaryPage(page => Math.max(0, page - 1))} disabled={loadingDictionary || dictionaryPage === 0}>پەڕەی پێشوو</button>
          <span>پەڕەی {kuNum(dictionaryPage + 1)} لە {kuNum(Math.max(1, Math.ceil(dictionaryTotal / pageSize)))}</span>
          <button type="button" onClick={() => setDictionaryPage(page => page + 1)} disabled={loadingDictionary || (dictionaryPage + 1) * pageSize >= dictionaryTotal}>پەڕەی دواتر</button>
        </div>
      </section>

      <form className="admin-form update-form" onSubmit={installUpdate}>
        <div className="admin-heading"><span>٤</span><div><h2>نوێکردنەوەی واتا</h2><p>ZIP ـی فەرمی واتا بار بکە؛ پشکنین و backup و دامەزراندن خۆکارانە ئەنجام دەدرێن.</p></div></div>
        <label className="file-picker">فایلی نوێکردنەوە
          <input type="file" accept=".zip,application/zip" onChange={e => setUpdateFile(e.target.files?.[0] || null)} />
          <span>{updateFile ? updateFile.name : "هیچ فایلێک هەڵنەبژێردراوە"}</span>
        </label>
        <small>تەنها ZIP ـێک بەکاربهێنە کە بۆ نوێکردنەوەی واتا ئامادە کراوە. سنووری قەبارە ٣٠ مێگابایتە.</small>
        <button disabled={!updateFile || updating}>{updating ? "نوێکردنەوە لە جێبەجێکردندایە…" : "پشکنین و دامەزراندن"}</button>
      </form>
      {message && <p className={message.includes("سەرکەوتوو نەبوو") || message.includes("تکایە") ? "error admin-message" : "success admin-message"}>{message}</p>}
    </div>}
  </Panel>;
}
